/**
 * ML Header — desktop menu panel, mobile drawer, predictive search
 */
(function () {
  const DESKTOP_MIN = 1320;
  const root = document.querySelector('[data-ml-header]');
  if (!root) return;

  const isDesktop = () => window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`).matches;

  const menuToggle = root.querySelector('[data-ml-open-menu]');
  const navRow = root.querySelector('.ml-header__nav-row');
  const menuPanel = root.querySelector('[data-ml-menu-panel]');
  const categoryButtons = menuPanel?.querySelectorAll('[data-ml-menu-cat]') ?? [];
  const menuSections = menuPanel?.querySelectorAll('[data-ml-menu-section]') ?? [];

  const setMenuPanelWidth = () => {
    if (!navRow) return;

    const rect = navRow.getBoundingClientRect();
    const sidebarWidth = 160;
    const tileMinWidth = 112;
    const panelPaddingX = 56;
    const gridGaps = 32;
    const minWidthForThreeCols = sidebarWidth + panelPaddingX + tileMinWidth * 3 + gridGaps;
    const navPercentWidth = rect.width * 0.45;

    navRow.style.setProperty(
      '--ml-menu-panel-width',
      `${Math.max(minWidthForThreeCols, navPercentWidth)}px`
    );
  };

  const activateCategory = (button) => {
    if (!(button instanceof HTMLButtonElement)) return;

    categoryButtons.forEach((cat) => {
      const isActive = cat === button;
      cat.classList.toggle('is-active', isActive);
      cat.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });

    const panelId = button.getAttribute('aria-controls');
    menuSections.forEach((section) => {
      const isActive = section.id === panelId;
      section.classList.toggle('is-active', isActive);
      section.hidden = !isActive;
    });
  };

  const openMenuPanel = () => {
    if (!menuPanel || !isDesktop()) return;

    setMenuPanelWidth();
    menuPanel.hidden = false;
    menuPanel.setAttribute('aria-hidden', 'false');
    menuToggle?.setAttribute('aria-expanded', 'true');
    menuToggle?.classList.add('is-active');
  };

  const closeMenuPanel = () => {
    if (!menuPanel) return;

    menuPanel.hidden = true;
    menuPanel.setAttribute('aria-hidden', 'true');
    menuToggle?.setAttribute('aria-expanded', 'false');
    menuToggle?.classList.remove('is-active');
  };

  const toggleMenuPanel = () => {
    if (!menuPanel || !isDesktop()) return;
    if (menuPanel.hidden) {
      openMenuPanel();
    } else {
      closeMenuPanel();
    }
  };

  const isOutsideMenuPanel = (target) => {
    if (!(target instanceof Node)) return false;
    if (menuPanel?.contains(target)) return false;
    if (menuToggle?.contains(target)) return false;
    return true;
  };

  menuToggle?.addEventListener('click', (event) => {
    if (isDesktop()) {
      event.stopPropagation();
      toggleMenuPanel();
      return;
    }

    root.querySelector('#Details-menu-drawer-container summary')?.click();
  });

  menuPanel?.querySelectorAll('[data-ml-menu-panel-close]').forEach((el) => {
    el.addEventListener('click', closeMenuPanel);
  });

  categoryButtons.forEach((button) => {
    button.addEventListener('click', () => activateCategory(button));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && menuPanel && !menuPanel.hidden) {
      closeMenuPanel();
    }
  });

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!menuPanel || menuPanel.hidden || !isDesktop()) return;
      if (isOutsideMenuPanel(event.target)) {
        closeMenuPanel();
      }
    },
    true
  );

  window.addEventListener('resize', () => {
    if (!isDesktop() && menuPanel && !menuPanel.hidden) {
      closeMenuPanel();
    } else if (menuPanel && !menuPanel.hidden) {
      setMenuPanelWidth();
    }
  });

  const headerMenu = root.querySelector('[data-ml-header-menu]');
  if (headerMenu) {
    const lockMenuHeightVars = () => {
      if (!isDesktop()) return;
      root.style.setProperty('--submenu-height', '0px');
      root.style.setProperty('--full-open-header-height', '0px');
    };

    lockMenuHeightVars();
    headerMenu.addEventListener('pointerover', lockMenuHeightVars, true);
    window.addEventListener('resize', lockMenuHeightVars);

    if (typeof MutationObserver !== 'undefined') {
      const menuHeightObserver = new MutationObserver(lockMenuHeightVars);
      menuHeightObserver.observe(root, { attributes: true, attributeFilter: ['style'] });
    }
  }

  const searchRoots = root.querySelectorAll('.ml-predictive-search');
  searchRoots.forEach((component) => {
    const input = component.querySelector('[ref="searchInput"]');
    if (!(input instanceof HTMLInputElement)) return;

    let emptyLoaded = false;

    const open = () => {
      component.classList.add('is-open');
      input.setAttribute('aria-expanded', 'true');

      if (!emptyLoaded && input.value.trim() === '') {
        emptyLoaded = true;
        if (typeof component.resetSearch === 'function') {
          component.resetSearch(false);
        }
      }
    };

    const close = () => {
      component.classList.remove('is-open');
      input.setAttribute('aria-expanded', 'false');
    };

    const isOutside = (target) => {
      return target instanceof Node && !component.contains(target);
    };

    input.addEventListener('focus', open);

    component.addEventListener('focusout', (event) => {
      const related = event.relatedTarget;
      if (related instanceof Node && component.contains(related)) return;
      window.setTimeout(() => {
        if (!component.matches(':focus-within')) close();
      }, 120);
    });

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (isOutside(event.target)) close();
      },
      true
    );

    component.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
  });

  const dockCart = document.querySelector('[data-ml-dock-cart]');
  const dockCartCount = dockCart?.querySelector('[data-ml-dock-cart-count]');

  const parseCartCount = (text) => parseInt(String(text).replace(/[^\d]/g, '') || '0', 10);

  const formatCartCount = (count) => (count > 99 ? '99+' : String(count));

  const renderDockCartCount = (count) => {
    if (!(dockCartCount instanceof HTMLElement)) return;
    dockCartCount.textContent = formatCartCount(count);
    dockCartCount.dataset.digits = String(formatCartCount(count).length);
  };

  if (dockCartCount) {
    renderDockCartCount(parseCartCount(dockCartCount.textContent));
  }

  document.addEventListener('cart:update', (event) => {
    const detail = event.detail?.data ?? {};
    const itemCount = detail.itemCount ?? 0;
    const comingFromProductForm = detail.source === 'product-form-component';
    const storedCount = sessionStorage.getItem('cart-count');

    let nextCount = itemCount;
    if (comingFromProductForm && dockCartCount) {
      nextCount = parseCartCount(dockCartCount.textContent) + itemCount;
    } else if (storedCount) {
      try {
        const { value } = JSON.parse(storedCount);
        nextCount = parseInt(value, 10);
      } catch (_) {
        /* no-op */
      }
    }

    if (Number.isFinite(nextCount) && nextCount >= 0) {
      renderDockCartCount(nextCount);
    }
  });
})();
